import { ChangeEvent } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'

import { FormDatePicker } from '@components/Form/FormDatePicker'
import { FormInput } from '@components/Form/FormInput'
import { FormSelect } from '@components/Form/FormSelect'
import { FormSwitch } from '@components/Form/FormSwitch'
import { FormTextArea } from '@components/Form/FormTextArea'
import { FormHeader } from '@containers/Headers/FormHeader'
import { useFindExceptionEventTypes } from '@hooks/catalogs/exceptionEventTypes'
import { IGenericTask } from '@interfaces/task/generic/IGenericTask'

import { useResetPullAheadRange } from './resetPullAheadRange'
import { TResetForm, useResetPullAheadForm } from './schema'

interface IProps {
  task: IGenericTask
  onClose: () => void
  onSave: (data: TResetForm) => void
}

export function ResetPullAheadForm({ task, onClose, onSave }: IProps) {
  const { data: rawExceptionEventTypes } = useFindExceptionEventTypes()
  const exceptionEventTypes = rawExceptionEventTypes ?? []

  const { getResolvedResetPullAheadSchema, handleSuperRefine } =
    useResetPullAheadForm()
  const { isPullAhead, maxDate, minDate } = useResetPullAheadRange()

  const datePickerLabel = isPullAhead
    ? 'Proposed Start Time'
    : 'ISTe Constraint Date'

  const type = isPullAhead ? 'Pull Ahead' : 'Reset'

  const exceptionEventTypesOptions = exceptionEventTypes
    .filter((eet) => eet.isActive)
    .map((eet) => {
      return {
        label: eet.description,
        value: eet.id,
      }
    })
    .sort()

  const defaultValues = {
    isPartial: false,
    hoursToMove: undefined,
    exceptionEventTypeId: '',
    comment: '',
  }

  const schema = getResolvedResetPullAheadSchema(isPullAhead)

  const validationSchema = schema.superRefine((val, ctx) => {
    if (isPartialWatch && val.hoursToMove) {
      if (val.hoursToMove < 0 || val.hoursToMove > task.duration) {
        ctx.addIssue(
          handleSuperRefine(`Hours should be between 0 and ${task.duration}`),
        )
      }
    }
    if (isPartialWatch && val.hoursToMove === undefined) {
      ctx.addIssue(handleSuperRefine('Please, add how many hours to move'))
    }
  })

  const {
    control,
    handleSubmit,
    resetField,
    formState: { isDirty },
    watch,
  } = useForm<TResetForm>({
    resolver: zodResolver(validationSchema),
    defaultValues,
  })

  const isPartialWatch = watch('isPartial')

  const handleResetField = (param: boolean) => {
    if (!param) {
      resetField('hoursToMove')
    }
  }

  return (
    <FormHeader
      leftLabel="Close"
      rightLabel="Save"
      onPressLeft={onClose}
      onPressRight={handleSubmit(onSave)}
      isRightButtonDisabled={!isDirty}
      title={type}
    >
      <div>
        <Controller
          control={control}
          name="isPartial"
          render={({ field: { value, onChange } }) => (
            <FormSwitch
              mb={4}
              onChange={(value: boolean) => handleResetField(value)}
              label="Is partial?"
              onToggle={onChange}
              value={value}
            />
          )}
        />

        <Controller
          control={control}
          name="exceptionEventTypeId"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormSelect
              mb={4}
              isRequired
              errorMessage={error?.message}
              placeholder="Select"
              label="Exception Event Type"
              items={exceptionEventTypesOptions}
              onValueChange={(value) => onChange(value)}
              selectedValue={value?.toString()}
            />
          )}
        />

        <Controller
          control={control}
          name="proposedStartTime"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormDatePicker
              label={datePickerLabel}
              isRequired
              onChange={onChange}
              value={value}
              mode="date"
              errorMessage={error?.message}
              minimumDate={minDate}
              maximumDate={maxDate}
            />
          )}
        />

        {isPartialWatch && (
          <Controller
            control={control}
            name="hoursToMove"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormInput
                isRequired
                label="Hours to move "
                placeholder={`Total duration is ${task.duration}`}
                errorMessage={error?.message}
                onChange={(e) => onChange(e.target.value)}
                value={value?.toString()}
              />
            )}
          />
        )}

        <Controller
          control={control}
          defaultValue={''}
          name="comment"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormTextArea
              placeholder="Enter your comments"
              label="Comments "
              errorMessage={error?.message}
              onChange={(e) => onChange(e.target.value)}
              value={value}
            />
          )}
        />
      </div>
    </FormHeader>
  )
}
